import { yupResolver } from '@hookform/resolvers/yup';
import i18n from 'app-i18n';
import {
  getInitialLanguageCode,
  makeDisplayInformationSchema,
} from 'components/forms/fields/display-information-field/display-information-field.utils';
import { useForm } from 'react-hook-form';
import { boolean, number, string } from 'yup';
import { object } from 'yup';
import { OrderOptionDrawerDataType } from '../../drawers/order-option-drawer/order-option-drawer.component';
import { useEffect } from 'react';

export const useOrderOptionForm = (
  data: OrderOptionDrawerDataType,
  context: any,
) => {
  const form = useForm({
    resolver: yupResolver(orderOptionSchema),
    defaultValues: orderOptionSchema.cast(
      data?.orderOption ?? { displayRank: data?.index },
    ),
    context,
  });

  useEffect(() => {
    form.reset(
      orderOptionSchema.cast(data?.orderOption ?? { displayRank: data?.index }),
    );
  }, [data?.index, data?.orderOption, form]);

  return { form };
};

const displayInfoField = (field: string) =>
  string()
    .test(
      'isEnabled',
      () => i18n.t('forms.errors.mixed.required'),
      function (value) {
        // @ts-ignore
        return !(this.from[1].value[field] && !value);
      },
    )
    .flexDefault('');

export const orderOptionSchema = object({
  optionName: string().required().default('').max(100),
  displayRank: number().required().default(0),

  enableOrderReceivedEmail: boolean().default(false),
  enableOrderReceivedSMS: boolean().default(false),
  enableOrderCompleteEmail: boolean().default(false),
  enableOrderCompleteSMS: boolean().default(false),
  enableImHere: boolean().default(false),
  enableInformationField: boolean().default(false),
  makeInformationFieldMandatory: boolean().default(false),
  enableInstructions: boolean().default(false),

  displayMessage: makeDisplayInformationSchema(
    object({
      title: string().required().max(100),

      orderConfirmationMessage: displayInfoField(
        'enableOrderReceivedEmail',
      ).max(500),
      smsOrderConfirmationMessage: displayInfoField('enableOrderReceivedSMS')
        .max(1000)
        .test(
          'max-segments',
          () =>
            i18n.t(
              'forms.orderOptionForm.fields.smsOrderConfirmationMessage.errors.maxSegments',
            ),
          (_value, context) => {
            return (
              (context?.options?.context?.smsOrderConfirmationMessages ?? 0) < 5
            );
          },
        ),
      orderClosedMessage: displayInfoField('enableOrderCompleteEmail').max(500),
      smsOrderClosedMessage: displayInfoField('enableOrderCompleteSMS')
        .max(1000)
        .test(
          'max-segments',
          () =>
            i18n.t(
              'forms.orderOptionForm.fields.smsOrderClosedMessage.errors.maxSegments',
            ),
          (_value, context) => {
            return (context?.options?.context?.smsOrderClosedMessages ?? 0) < 5;
          },
        ),
      imHereResponseMessage: displayInfoField('enableImHere').max(500),
      informationField: displayInfoField('enableInformationField').max(35),
      errorMessageIfInformationFieldIsEmpty: displayInfoField(
        'makeInformationFieldMandatory',
      ).max(500),
      instructions: displayInfoField('enableInstructions').max(500),
    }),
    [
      {
        languageCode: getInitialLanguageCode(
          navigator.language.split('-')[0].toUpperCase(),
        ),
        title: '',
        orderConfirmationMessage:
          'Thank you for placing an order at our [#Location#] location, [#Customer#]! Your order has been received. Your [#Order Option Display Title#] order will be completed at [#Required Time#].',
        smsOrderConfirmationMessage:
          'Your Order: [#Order Number#] will be ready at [#Required Time#].',
        orderClosedMessage:
          'Thank you for placing an order at our [#Location#] location, [#Customer#]! Your order has been received. Your [#Order Option Display Title#] order will be completed at [#Required Time#].',
        smsOrderClosedMessage:
          'Your Order: [#Order Number#] will be ready at [#Required Time#].',
        imHereResponseMessage: '',
        informationField: '',
        errorMessageIfInformationFieldNotEmpty: '',
      },
    ],
  ),
});
